import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from "@/Components/ui/input";
import { Button } from "@/Components/ui/button"; 
import { ChevronRight } from "lucide-react";
import axiosInstance from '../utils/axiosConfig';
import logo from '../images/pslogo.png';

const Login = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        setError(''); // Clear error when user types
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        try {
            const response = await axiosInstance.post('/auth/login', formData);

            if (response.data && response.data.accesstoken) {
                localStorage.setItem('token', response.data.accesstoken);
                localStorage.setItem('refreshToken', response.data.refreshtoken);
                localStorage.setItem('expiresIn', response.data.expiresin);

                if (response.data.menu) {
                    localStorage.setItem('selectedMenu', response.data.menu.id);
                    localStorage.setItem('selectedMenuName', response.data.menu.name);
                }

                navigate('/dashboard');
            } else {
                setError('Er ging iets mis bij het inloggen.');
                setIsLoading(false); // Reset loading state
            }
        } catch (err) {
            console.log('Login error:', err); // Debug logging
            setIsLoading(false); // Reset loading state

            if (err.response) { 
                setError(err.response.data || 'Ongeldige email of wachtwoord.');
            } else if (err.request) { 
                setError('Kan geen verbinding maken met de server.');
            } else { 
                setError('Er ging iets mis bij het inloggen.');
            }
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 "> 
            <div className="max-w-md w-full space-y-6 p-8 bg-white rounded-lg shadow-lg relative">
                <div className="absolute left-[15px] top-[-5px] w-[80px]">
                    <img src={logo} />
                </div>
                {/* Header Section */}
                <div className="text-center">
                    <h2 className="text-3xl font-inter text-ps-blue-dark">
                        PS Menu Maker
                    </h2>
                    <p className="mt-2 text-sm text-gray-600 font-inter">
                        Log in om verder te gaan
                    </p>
                </div>

                {/* Form Section */}
                <form onSubmit={handleSubmit} className="mt-8 space-y-6">
                    {/* Error Display */}
                    {error && (
                        <div className="text-red-600 text-sm text-center bg-red-50 p-3 rounded font-inter">
                            {error}
                        </div>
                    )}

                    {/* Email Field */}
                    <div>
                        <label className="block text-sm font-inter text-ps-blue-dark mb-2">
                            E-mailadres
                        </label>
                        <Input
                            type="email"
                            name="email"
                            required
                            value={formData.email}
                            onChange={handleChange}
                            className="w-full p-2 border rounded bg-white font-inter"
                            placeholder="naam@bedrijf.nl"
                        />
                    </div>

                    {/* Password Field */}
                    <div>
                        <label className="block text-sm font-inter text-ps-blue-dark mb-2">
                            Wachtwoord
                        </label>
                        <Input
                            type="password"
                            name="password"
                            required
                            value={formData.password}
                            onChange={handleChange}
                            className="w-full p-2 border rounded bg-white font-inter"
                            placeholder="••••••••"
                            minLength={8}
                        />
                    </div>

                    {/* Forgot Password Link */}
                    <div className="flex items-center justify-between">
                        <Button
                            type="button"
                            variant="link"
                            onClick={() => navigate('/forgot-password')}
                            className="text-ps-blue-dark hover:text-ps-blue text-sm font-inter"
                        >
                            Wachtwoord vergeten?
                        </Button>
                    </div>

                    {/* Submit Button */}
                    <div>
                        <Button
                            type="submit"
                            disabled={isLoading}
                            className="w-full bg-ps-blue-dark hover:opacity-80 text-white flex items-center justify-center gap-2 h-12 font-inter"
                        >
                            {isLoading ? 'Bezig met inloggen...' : 'Inloggen'}
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>

                    {/* Register Link */}
                    <div className="text-center">
                        <p className="text-sm text-gray-600 font-inter">
                            Nog geen account?{' '}
                            <Button
                                type="button"
                                variant="link"
                                onClick={() => navigate('/register')}
                                className="text-ps-blue-dark hover:text-ps-blue font-medium"
                            >
                                Registreer hier
                            </Button>
                        </p>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default Login;